import { type HandoverGeneralCheck, INSPECTION_STATUS_LABELS, progressOf } from '@sivan/shared';
import { useId } from 'react';
import { ResultSegment } from '../ui/ResultSegment';
import { ProgressBar } from './InspectionPanel';
import { type DraftCheck, validateCheck } from './handover-draft';
import { INSPECTION_SEGMENTS, chosen } from './results';

// "בדיקות ומסירה כללית" (H-Next): the handover's general checks (keys, remotes, parking gate,
// parking and storage, spare tiles, ...), each תקין / לא תקין / לא רלוונטי, with a quantity where
// the check counts one, and a note.

export const checkElementId = (id: string) => `general-check-${id}`;
export const CHECKS_SECTION_ID = 'general-checks';

export function GeneralChecksPanel({
  items,
  checks,
  readOnly,
  showErrors,
  starting,
  canStart,
  onChange,
  onStart,
}: {
  readonly items: readonly HandoverGeneralCheck[];
  readonly checks: Readonly<Record<string, DraftCheck>>;
  readonly readOnly: boolean;
  readonly showErrors: boolean;
  readonly starting: boolean;
  /** The draft has no checks yet and may get them (saved, editable). */
  readonly canStart: boolean;
  readonly onChange: (id: string, change: Partial<DraftCheck>) => void;
  readonly onStart: () => void;
}) {
  const titleId = useId();
  const current = items.map((c) => ({
    ...c,
    ...(checks[c.id] ?? { status: c.status, quantity: '', note: '' }),
  }));
  const progress = progressOf(current);
  return (
    <section
      id={CHECKS_SECTION_ID}
      className="editor-section general-checks"
      aria-labelledby={titleId}
      tabIndex={-1}
    >
      <div className="inspection__head">
        <h2 id={titleId} className="section-title">
          בדיקות ומסירה כללית
        </h2>
        {items.length > 0 && (
          <>
            <ProgressBar progress={progress} label="התקדמות הבדיקות הכלליות" />
            <p className="inspection__summary">
              נבדקו {progress.checked} מתוך {progress.total}
              {progress.defects > 0 && ` · ${String(progress.defects)} לא תקין`}
            </p>
          </>
        )}
      </div>
      {items.length === 0 ? (
        <>
          <p className="hint">
            אין למסירה זו בדיקות כלליות. הן מוגדרות בניהול נתוני אב ← הגדרות מסירות ← בדיקות ומסירה
            כללית.
          </p>
          {!readOnly && canStart && (
            <button
              type="button"
              className="button button--primary"
              disabled={starting}
              onClick={onStart}
            >
              {starting ? 'מוסיף…' : 'הוספת הבדיקות הכלליות של הפרויקט'}
            </button>
          )}
        </>
      ) : (
        <ul className="check-list general-check-list">
          {current.map((c) => {
            const errors = showErrors ? validateCheck(c) : {};
            const noteMissing = c.status === 'defect' && c.note.trim() === '';
            return (
              <li
                key={c.id}
                id={checkElementId(c.id)}
                tabIndex={-1}
                className={`check check--${c.status} general-check`}
                aria-label={c.name}
              >
                <div className="check__row">
                  <span className="check__name">
                    {c.name}
                    {c.status === 'unchecked' && (
                      <span className="chip chip--pending">
                        {INSPECTION_STATUS_LABELS.unchecked}
                      </span>
                    )}
                  </span>
                  <ResultSegment
                    label={`תוצאת בדיקה: ${c.name}`}
                    segments={INSPECTION_SEGMENTS}
                    value={chosen(c.status)}
                    disabled={readOnly}
                    onChoose={(status) => {
                      onChange(c.id, { status: c.status === status ? 'unchecked' : status });
                    }}
                  />
                </div>
                <div className="general-check__fields">
                  {c.quantityUnit !== null && (
                    <label className="field general-check__quantity">
                      <span>כמות ({c.quantityUnit})</span>
                      <input
                        value={c.quantity}
                        inputMode="numeric"
                        dir="ltr"
                        disabled={readOnly}
                        aria-invalid={errors.quantity !== undefined}
                        onChange={(e) => {
                          onChange(c.id, { quantity: e.target.value });
                        }}
                      />
                      {errors.quantity !== undefined && (
                        <p className="field__error">{errors.quantity}</p>
                      )}
                    </label>
                  )}
                  <label className="field general-check__note">
                    <span>{c.status === 'defect' ? 'מה לא תקין' : 'הערה'}</span>
                    <input
                      value={c.note}
                      disabled={readOnly}
                      aria-invalid={errors.note !== undefined}
                      onChange={(e) => {
                        onChange(c.id, { note: e.target.value });
                      }}
                    />
                    {errors.note !== undefined && <p className="field__error">{errors.note}</p>}
                    {noteMissing && !readOnly && (
                      <p className="hint">כדאי לפרט מה לא תקין; הסיכום יציין שחסר פירוט.</p>
                    )}
                  </label>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
